
import { Component, Prop, Vue } from "vue-property-decorator";
import { OffSiteRates } from "@/utils/offSiteRates";
import GoogleAddressAutocomplete from "@/components/common/GoogleAddressAutocomplete.vue";
import { ValidationProvider } from "vee-validate";
import moment from "moment";

type ExpenseKey = "tollsAmount" | "otherAmount";

type AllowanceKey = "sntIncluded" | "breakfastIncluded" | "dinnerIncluded";

type QuantityKey = "sntQty" | "breakfastQty" | "dinnerQty";

// Costs that can be settled before the course runs. Toll gates are absent on
// purpose: the amount is only known after the trip.
type PrepayKey =
  | "prepayTravel"
  | "prepaySnt"
  | "prepayBreakfast"
  | "prepayDinner"
  | "prepayAccommodation"
  | "prepayOther";

@Component({ components: { GoogleAddressAutocomplete, ValidationProvider } })
export default class EventExpenseSetup extends Vue {
  checkInMenu = false;
  checkOutMenu = false;
  @Prop({ default: false }) readonly customerAccommodationIncluded!: boolean;
  @Prop({ default: 0 }) readonly customerAccommodationAmount!: number | string;
  @Prop({ default: "" }) readonly accommodationName!: string;
  @Prop({ default: "" }) readonly accommodationAddress!: string;
  @Prop({ default: "" }) readonly accommodationCheckIn!: string;
  @Prop({ default: "" }) readonly accommodationCheckOut!: string;
  @Prop({ default: 0 }) readonly sntQty!: number;
  @Prop({ default: 0 }) readonly breakfastQty!: number;
  @Prop({ default: 0 }) readonly dinnerQty!: number;
  @Prop({ default: 1 }) readonly durationDays!: number;
  @Prop({ default: () => ({}) }) readonly rates!: Partial<OffSiteRates>;
  @Prop({ default: false }) readonly sntIncluded!: boolean;
  @Prop({ default: false }) readonly breakfastIncluded!: boolean;
  @Prop({ default: false }) readonly dinnerIncluded!: boolean;
  @Prop({ default: 0 }) readonly trainerAccommodationAmount!: number;
  @Prop({ default: 0 }) readonly tollsAmount!: number;
  @Prop({ default: 0 }) readonly otherAmount!: number;
  @Prop({ default: "" }) readonly otherDescription!: string;
  @Prop({ default: false }) readonly disabled!: boolean;
  @Prop({ default: false }) readonly canManageTolls!: boolean;
  @Prop({ default: false }) readonly prepayTravel!: boolean;
  @Prop({ default: false }) readonly prepaySnt!: boolean;
  @Prop({ default: false }) readonly prepayBreakfast!: boolean;
  @Prop({ default: false }) readonly prepayDinner!: boolean;
  @Prop({ default: false }) readonly prepayAccommodation!: boolean;
  @Prop({ default: false }) readonly prepayOther!: boolean;
  // True once the prepayment invoice has been approved: its figures are frozen,
  // so the fields it was raised from stop accepting edits.
  @Prop({ default: false }) readonly prepaymentLocked!: boolean;
  @Prop({ default: false }) readonly uploading!: boolean;
  @Prop({ default: () => [] }) readonly tollDocuments!: any[];

  included: Record<ExpenseKey, boolean> = {
    tollsAmount: false,
    otherAmount: false
  };

  // Allowances are charged per booking day at the configured rate, so the
  // booking only records whether each one applies.
  allowances: Array<{
    key: AllowanceKey;
    qtyKey: QuantityKey;
    prepayKey: PrepayKey;
    rateKey: keyof OffSiteRates;
    label: string;
  }> = [
    {
      key: "sntIncluded",
      qtyKey: "sntQty",
      prepayKey: "prepaySnt",
      rateKey: "sntPerNight",
      label: "SNT"
    },
    {
      key: "breakfastIncluded",
      qtyKey: "breakfastQty",
      prepayKey: "prepayBreakfast",
      rateKey: "breakfastPerNight",
      label: "Breakfast allowance"
    },
    {
      key: "dinnerIncluded",
      qtyKey: "dinnerQty",
      prepayKey: "prepayDinner",
      rateKey: "dinnerPerNight",
      label: "Dinner allowance"
    }
  ];

  quantityValue(key: QuantityKey): number {
    const values: Record<QuantityKey, number> = {
      sntQty: this.sntQty,
      breakfastQty: this.breakfastQty,
      dinnerQty: this.dinnerQty
    };
    return Number(values[key] || 0);
  }

  rateFor(key: keyof OffSiteRates): number {
    return Number((this.rates as any)[key] || 0);
  }

  // Switching an allowance on starts it at the booking duration, which is the
  // usual case, and leaves the user free to change it.
  toggleAllowance(
    allowance: { key: AllowanceKey; qtyKey: QuantityKey },
    included: boolean
  ): void {
    this.$emit(`update:${allowance.key}`, included);
    if (included) {
      if (this.quantityValue(allowance.qtyKey) < 1) {
        this.$emit(
          `update:${allowance.qtyKey}`,
          Math.max(1, Number(this.durationDays) || 1)
        );
      }
    } else {
      this.$emit(`update:${allowance.qtyKey}`, 0);
    }
  }

  updateQuantity(key: QuantityKey, value: string): void {
    this.$emit(`update:${key}`, Math.max(0, Math.floor(Number(value) || 0)));
  }

  formatMoney(value: number): string {
    return `R ${new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(value || 0))}`;
  }

  pendingFile: File | null = null;

  documentHeaders = [
    { text: "Document", value: "fileName", align: "start" },
    { text: "Size", value: "size", align: "start", width: "110px" },
    { text: "Uploaded", value: "uploadedAt", align: "start", width: "150px" },
    {
      text: "Actions",
      value: "actions",
      align: "start",
      sortable: false,
      width: "110px"
    }
  ];

  onFilesSelected(selection: File | File[] | null): void {
    const file = Array.isArray(selection) ? selection[0] : selection;
    this.pendingFile = null;
    if (file) {
      this.$emit("upload-documents", [file]);
    }
  }

  prepayValue(key: PrepayKey): boolean {
    const values: Record<PrepayKey, boolean> = {
      prepayTravel: this.prepayTravel,
      prepaySnt: this.prepaySnt,
      prepayBreakfast: this.prepayBreakfast,
      prepayDinner: this.prepayDinner,
      prepayAccommodation: this.prepayAccommodation,
      prepayOther: this.prepayOther
    };
    return Boolean(values[key]);
  }

  togglePrepay(key: PrepayKey, value: boolean): void {
    this.$emit(`update:${key}`, Boolean(value));
  }

  get fieldsDisabled(): boolean {
    return this.disabled || this.prepaymentLocked;
  }

  formatSize(bytes: number): string {
    const size = Number(bytes || 0);
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  allowanceValue(key: AllowanceKey): boolean {
    const values: Record<AllowanceKey, boolean> = {
      sntIncluded: this.sntIncluded,
      breakfastIncluded: this.breakfastIncluded,
      dinnerIncluded: this.dinnerIncluded
    };
    return Boolean(values[key]);
  }

  toggleCustomerAccommodation(included: boolean): void {
    this.$emit("update:customerAccommodationIncluded", included);
    if (!included) {
      this.$emit("update:customerAccommodationAmount", 0);
      this.$emit("update:trainerAccommodationAmount", 0);
      this.$emit("update:accommodationName", "");
      this.$emit("update:accommodationAddress", "");
      this.$emit("update:accommodationCheckIn", "");
      this.$emit("update:accommodationCheckOut", "");
    }
  }

  expenseValue(key: ExpenseKey): number {
    const values: Record<ExpenseKey, number> = {
      tollsAmount: this.tollsAmount,
      otherAmount: this.otherAmount
    };
    return Number(values[key] || 0);
  }

  isIncluded(key: ExpenseKey): boolean {
    return this.included[key];
  }

  toggleExpense(key: ExpenseKey, included: boolean): void {
    this.$set(this.included, key, included);
    if (!included) {
      this.updateExpense(key, 0);
      if (key === "otherAmount") {
        this.$emit("update:otherDescription", "");
      }
    }
  }

  updateExpense(key: ExpenseKey, value: number | string): void {
    this.$emit(`update:${key}`, Number(value || 0));
  }

  formatDate(value: string): string {
    return value ? moment(value).format("DD/MM/YYYY") : "";
  }

  updateCheckIn(value: string): void {
    this.$emit("update:accommodationCheckIn", value);
    this.checkInMenu = false;
  }

  updateCheckOut(value: string): void {
    this.$emit("update:accommodationCheckOut", value);
    this.checkOutMenu = false;
  }

  created(): void {
    (Object.keys(this.included) as ExpenseKey[]).forEach(key => {
      this.$set(this.included, key, this.expenseValue(key) > 0);
    });
  }
}
